import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Drawer } from '@/components/shell/Drawer';
import { BadgeCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { TabError } from '@/screens/user-detail/TabError';
import { formatDateTime, humanise } from '@/lib/format';
import { Field, Fields, Panel, TabSkeleton } from '@/components/panel/panels';

import { DELIVERY_STATUS_LABELS, DELIVERY_STATUS_TONES, eventTypeLabel } from './notification-labels';
import { mutateNotificationDelivery, notificationDeliveryQueryOptions } from './notifications-query';

export function NotificationDeliveryDrawer({
  deliveryId,
  onClose,
}: {
  readonly deliveryId: string;
  readonly onClose: () => void;
}) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery(notificationDeliveryQueryOptions(api, deliveryId));
  const [action, setAction] = useState<'retry' | 'suppress' | null>(null);
  const mutation = useMutation({
    mutationFn: (next: 'retry' | 'suppress') => mutateNotificationDelivery(api, deliveryId, next),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'notification-delivery', deliveryId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'notification-metrics'] });
      setAction(null);
    },
    retry: false,
  });
  const delivery = query.data;
  return (
    <>
      <Drawer
        title={`Delivery · ${deliveryId}`}
        subtitle="Delivery state only, no message content"
        onClose={onClose}
        footer={
          delivery === undefined ? undefined : (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={!can.mutate || !delivery.retryable || mutation.isPending}
                title={can.mutate ? (delivery.retryBlockedReason ?? undefined) : 'Requires the admin role.'}
                onClick={() => {
                  setAction('retry');
                }}
              >
                Retry…
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!can.mutate || !delivery.suppressible || mutation.isPending}
                title={
                  can.mutate ? (delivery.suppressBlockedReason ?? undefined) : 'Requires the admin role.'
                }
                onClick={() => {
                  setAction('suppress');
                }}
                className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
              >
                Suppress…
              </Button>
            </>
          )
        }
      >
        {query.isPending ? <TabSkeleton rows={5} /> : null}
        {query.isError ? (
          <TabError message={query.error.message} onRetry={() => void query.refetch()} />
        ) : null}
        {delivery === undefined ? null : (
          <div className="flex flex-col gap-5">
            <div className="flex gap-2">
              <BadgeCell
                label={DELIVERY_STATUS_LABELS[delivery.status]}
                tone={DELIVERY_STATUS_TONES[delivery.status]}
                dot
              />
              <BadgeCell label={eventTypeLabel(delivery.eventType)} tone="info" />
            </div>
            <Panel title="Correlation">
              <Fields>
                <Field label="Delivery" value={delivery.id} mono />
                <Field label="Notification" value={delivery.notificationId} mono />
                <Field label="Outbox" value={delivery.outboxId} mono />
                <Field label="Installation" value={delivery.installationId} mono />
              </Fields>
            </Panel>
            <Panel title="Delivery">
              <Fields>
                <Field label="Category" value={humanise(delivery.category)} />
                <Field label="Priority" value={delivery.priority} />
                <Field label="Due" value={formatDateTime(delivery.dueAt)} />
                <Field
                  label="Sent"
                  value={delivery.sentAt === null ? null : formatDateTime(delivery.sentAt)}
                />
                <Field label="Expires" value={formatDateTime(delivery.expiresAt)} />
                <Field
                  label="Attempts"
                  value={`${String(delivery.attemptCount)} / ${String(delivery.maxAttempts)}`}
                />
                <Field label="Error class" value={delivery.lastErrorClass} />
                <Field label="Error code" value={delivery.lastErrorCode} mono />
                <Field label="Suppression" value={delivery.suppressionReason} />
              </Fields>
            </Panel>
            <Panel title="Installation state">
              <Fields>
                <Field label="Platform" value={delivery.installation.platform} />
                <Field label="Permission" value={delivery.installation.permission} />
                <Field label="Active" value={delivery.installation.active ? 'yes' : 'no'} />
                <Field label="Invalidation" value={delivery.installation.invalidationReason} />
              </Fields>
            </Panel>
            <Panel title={`Attempts (${String(delivery.attempts.length)})`}>
              {delivery.attempts.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">No provider attempt was made.</p>
              ) : (
                <ol className="flex flex-col gap-2">
                  {delivery.attempts.map((attempt) => (
                    <li key={attempt.id} className="rounded-md border border-border px-3 py-2 text-[13px]">
                      <span className="font-medium">
                        #{attempt.attemptNumber} · {humanise(attempt.status)}
                      </span>
                      <span className="ml-2 font-mono text-muted-foreground">
                        {attempt.errorCode ?? attempt.providerMessageId ?? '—'}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </Panel>
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              This view shows no notification text, no destination, no profile data and no push tokens.
            </p>
          </div>
        )}
      </Drawer>
      {action === null ? null : (
        <ConfirmDialog
          title={action === 'retry' ? 'Schedule one durable retry?' : 'Suppress this delivery?'}
          body={
            action === 'retry'
              ? 'Nothing is sent from here. The normal worker picks it up and re-checks the current settings, whether the thing it was about still matters, and whether it is too old, before it tries again.'
              : 'This delivery is finished for good. A worker that has it queued will load it and stop before sending.'
          }
          confirmLabel={action === 'retry' ? 'Schedule retry' : 'Suppress'}
          destructive={action === 'suppress'}
          typeToConfirm={deliveryId}
          isPending={mutation.isPending}
          {...(mutation.isError ? { error: mutation.error.message } : {})}
          onConfirm={() => {
            mutation.mutate(action);
          }}
          onCancel={() => {
            setAction(null);
          }}
        />
      )}
    </>
  );
}
